import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { FiSettings, FiDatabase, FiAlertTriangle, FiShield, FiChevronDown, FiChevronRight, FiMail } from 'react-icons/fi';
import './Configuracoes.css';

const CONFIRMACAO_ESPERADA = 'REDEFINIR_BASE';

const LABELS_RECURSO = {
  users: 'Usuários',
  roles: 'Papéis',
  permissions: 'Permissões',
  enterprise: 'Empreendimentos',
  license: 'Licenças',
  condition: 'Condicionantes',
  task: 'Tarefas',
  evidence: 'Evidências',
  audit: 'Auditoria',
  report: 'Relatórios'
};

const Configuracoes = () => {
  const { user, refreshPermissions } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [confirmacao, setConfirmacao] = useState('');
  const [showResetModal, setShowResetModal] = useState(false);

  // Permissões
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [permsLoading, setPermsLoading] = useState(false);
  const [permsSaving, setPermsSaving] = useState(null);
  const [expandedRoles, setExpandedRoles] = useState({});
  const [localPerms, setLocalPerms] = useState({}); // { roleId: Set(permission_ids) }

  // SMTP
  const [smtp, setSmtp] = useState({ smtp_host: '', smtp_port: '587', smtp_user: '', smtp_pass: '' });
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpTestando, setSmtpTestando] = useState(false);
  const [smtpMessage, setSmtpMessage] = useState(null);

  const isAdmin = user?.role === 'admin_sistema' || user?.email === 'admin@greencheck.com';

  const carregarInfo = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/admin/configuracoes');
      setInfo(res.data);
      if (res.data?.smtp) {
        setSmtp({
          smtp_host: res.data.smtp.smtp_host || '',
          smtp_port: res.data.smtp.smtp_port || '587',
          smtp_user: res.data.smtp.smtp_user || '',
          smtp_pass: ''
        });
      }
    } catch (err) {
      console.error('Erro ao carregar configurações:', err);
      if (err.response?.status === 403) {
        navigate('/home');
      } else {
        setInfo({ licencas: 0, condicionantes: 0, tarefas: 0, versao: '-' });
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const salvarSmtp = async (e) => {
    e.preventDefault();
    setSmtpMessage(null);
    try {
      setSmtpSaving(true);
      await api.put('/api/admin/configuracoes/smtp', {
        smtp_host: smtp.smtp_host.trim(),
        smtp_port: smtp.smtp_port.trim() || '587',
        smtp_user: smtp.smtp_user.trim(),
        smtp_pass: smtp.smtp_pass || undefined
      });
      setSmtpMessage({ type: 'success', text: 'Configuração SMTP salva. O sistema poderá enviar e-mails de lembrete, redefinição de senha, etc.' });
      setSmtp(prev => ({ ...prev, smtp_pass: '' }));
      carregarInfo();
    } catch (err) {
      setSmtpMessage({ type: 'error', text: err.response?.data?.error || 'Erro ao salvar SMTP' });
    } finally {
      setSmtpSaving(false);
    }
  };

  const enviarTesteSmtp = async () => {
    setSmtpMessage(null);
    try {
      setSmtpTestando(true);
      const res = await api.post('/api/admin/configuracoes/smtp/test');
      setSmtpMessage({ type: 'success', text: res.data?.message || 'E-mail de teste enviado. Verifique sua caixa de entrada.' });
    } catch (err) {
      setSmtpMessage({ type: 'error', text: err.response?.data?.error || 'Falha ao enviar e-mail de teste. Verifique a configuração SMTP.' });
    } finally {
      setSmtpTestando(false);
    }
  };

  const carregarPermissoes = useCallback(async () => {
    try {
      setPermsLoading(true);
      const [rolesRes, permsRes] = await Promise.all([
        api.get('/api/usuarios/roles'),
        api.get('/api/usuarios/permissions')
      ]);
      const rolesData = rolesRes.data || [];
      setRoles(rolesData);
      setPermissions(permsRes.data || []);
      const initial = {};
      rolesData.forEach(r => {
        initial[r.id] = new Set(r.permission_ids || []);
      });
      setLocalPerms(initial);
    } catch (err) {
      console.error('Erro ao carregar permissões:', err);
    } finally {
      setPermsLoading(false);
    }
  }, []);

  const toggleRoleExpanded = (roleId) => {
    setExpandedRoles(prev => ({ ...prev, [roleId]: !prev[roleId] }));
  };

  const togglePermission = (roleId, permissionId) => {
    setLocalPerms(prev => {
      const next = { ...prev };
      const set = new Set(next[roleId] || []);
      if (set.has(permissionId)) {
        set.delete(permissionId);
      } else {
        set.add(permissionId);
      }
      next[roleId] = set;
      return next;
    });
  };

  const toggleAllInResource = (roleId, resourcePerms, checked) => {
    setLocalPerms(prev => {
      const next = { ...prev };
      const set = new Set(next[roleId] || []);
      resourcePerms.forEach(p => {
        if (checked) set.add(p.id);
        else set.delete(p.id);
      });
      next[roleId] = set;
      return next;
    });
  };

  const salvarPermissoesRole = async (roleId) => {
    try {
      setPermsSaving(roleId);
      const permIds = Array.from(localPerms[roleId] || []);
      await api.put(`/api/usuarios/roles/${roleId}/permissions`, { permission_ids: permIds });
      await carregarPermissoes();
      if (refreshPermissions) refreshPermissions();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao salvar permissões');
    } finally {
      setPermsSaving(null);
    }
  };

  const permsPorRecurso = permissions.reduce((acc, p) => {
    const r = p.recurso || 'outros';
    if (!acc[r]) acc[r] = [];
    acc[r].push(p);
    return acc;
  }, {});

  useEffect(() => {
    if (isAdmin) {
      carregarInfo();
    } else {
      setLoading(false);
    }
  }, [isAdmin, carregarInfo]);

  useEffect(() => {
    if (isAdmin) carregarPermissoes();
  }, [isAdmin, carregarPermissoes]);

  const handleResetDb = async (e) => {
    e.preventDefault();
    if (confirmacao !== CONFIRMACAO_ESPERADA) {
      alert('Digite exatamente: REDEFINIR_BASE para confirmar.');
      return;
    }
    try {
      setResetting(true);
      await api.post('/api/admin/reset-db', { confirmacao });
      setShowResetModal(false);
      setConfirmacao('');
      carregarInfo();
      alert('Base de dados redefinida com sucesso.');
      navigate('/home');
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao redefinir base de dados');
    } finally {
      setResetting(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="configuracoes-page">
        <div className="configuracoes-error">
          <FiAlertTriangle size={48} />
          <h2>Acesso negado</h2>
          <p>Apenas administradores do sistema podem acessar as configurações.</p>
          <button className="btn btn-primary" onClick={() => navigate('/')}>
            Voltar ao início
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="configuracoes-page">
        <div className="configuracoes-loading">Carregando configurações…</div>
      </div>
    );
  }

  return (
    <div className="configuracoes-page">
      <header className="configuracoes-header">
        <h1 className="configuracoes-title">
          <FiSettings />
          Configurações do Sistema
        </h1>
      </header>

      <section className="configuracoes-section">
        <h2>Informações do sistema</h2>
        <div className="configuracoes-grid">
          <div className="configuracoes-card">
            <span className="configuracoes-card-label">Licenças cadastradas</span>
            <span className="configuracoes-card-value">{info?.licencas ?? 0}</span>
          </div>
          <div className="configuracoes-card">
            <span className="configuracoes-card-label">Condicionantes</span>
            <span className="configuracoes-card-value">{info?.condicionantes ?? 0}</span>
          </div>
          <div className="configuracoes-card">
            <span className="configuracoes-card-label">Tarefas</span>
            <span className="configuracoes-card-value">{info?.tarefas ?? 0}</span>
          </div>
          <div className="configuracoes-card">
            <span className="configuracoes-card-label">Versão</span>
            <span className="configuracoes-card-value">{info?.versao ?? '-'}</span>
          </div>
        </div>
      </section>

      <section className="configuracoes-section configuracoes-smtp">
        <h2>
          <FiMail />
          E-mail (SMTP)
        </h2>
        <p className="configuracoes-smtp-desc">
          Configure o servidor de e-mail para envio de lembretes de vencimento, redefinição de senha e outras notificações.
        </p>
        <form onSubmit={salvarSmtp} className="configuracoes-smtp-form">
          <div className="configuracoes-smtp-grid">
            <div className="form-group">
              <label htmlFor="smtp_host">Hostname</label>
              <input
                id="smtp_host"
                type="text"
                value={smtp.smtp_host}
                onChange={(e) => setSmtp(prev => ({ ...prev, smtp_host: e.target.value }))}
                placeholder="ex: smtp.gmail.com"
                className="form-control"
                autoComplete="off"
              />
            </div>
            <div className="form-group">
              <label htmlFor="smtp_port">Porta</label>
              <input
                id="smtp_port"
                type="text"
                value={smtp.smtp_port}
                onChange={(e) => setSmtp(prev => ({ ...prev, smtp_port: e.target.value }))}
                placeholder="587"
                className="form-control"
                autoComplete="off"
              />
            </div>
            <div className="form-group">
              <label htmlFor="smtp_user">Usuário</label>
              <input
                id="smtp_user"
                type="text"
                value={smtp.smtp_user}
                onChange={(e) => setSmtp(prev => ({ ...prev, smtp_user: e.target.value }))}
                placeholder="e-mail ou usuário SMTP"
                className="form-control"
                autoComplete="off"
              />
            </div>
            <div className="form-group">
              <label htmlFor="smtp_pass">Senha</label>
              <input
                id="smtp_pass"
                type="password"
                value={smtp.smtp_pass}
                onChange={(e) => setSmtp(prev => ({ ...prev, smtp_pass: e.target.value }))}
                placeholder={info?.smtp?.smtp_pass_set ? '•••••••• (deixe em branco para manter)' : 'Senha do e-mail ou app password'}
                className="form-control"
                autoComplete="new-password"
              />
            </div>
          </div>
          {smtpMessage && (
            <p className={`configuracoes-smtp-msg ${smtpMessage.type === 'error' ? 'configuracoes-smtp-msg-error' : ''}`}>
              {smtpMessage.text}
            </p>
          )}
          <div className="configuracoes-smtp-actions">
            <button type="submit" className="btn btn-primary" disabled={smtpSaving}>
              {smtpSaving ? 'Salvando…' : 'Salvar'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={enviarTesteSmtp}
              disabled={smtpTestando || !info?.smtp?.smtp_pass_set}
              title={!info?.smtp?.smtp_pass_set ? 'Salve a configuração SMTP antes de testar' : 'Envia um e-mail de teste para seu usuário'}
            >
              {smtpTestando ? 'Enviando…' : 'Enviar e-mail de teste'}
            </button>
          </div>
        </form>
      </section>

      <section className="configuracoes-section configuracoes-permissoes">
        <h2>
          <FiShield />
          Permissões por nível de usuário
        </h2>
        <p className="configuracoes-permissoes-desc">
          Configure quais acessos cada nível possui. Marque ou desmarque as permissões para cada papel.
        </p>
        {permsLoading ? (
          <div className="configuracoes-loading">Carregando permissões…</div>
        ) : (
          <div className="permissoes-roles">
            {roles.map((role) => {
              const isExpanded = expandedRoles[role.id] !== false;
              const rolePerms = localPerms[role.id] || new Set();
              const isAdminSistema = role.nome === 'admin_sistema';
              return (
                <div key={role.id} className={`permissoes-role-card ${isAdminSistema ? 'permissoes-role-admin' : ''}`}>
                  <div
                    className="permissoes-role-header"
                    onClick={() => toggleRoleExpanded(role.id)}
                  >
                    {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
                    <div className="permissoes-role-info">
                      <span className="permissoes-role-nome">{role.descricao || role.nome}</span>
                      <span className="permissoes-role-nivel">Nível {role.nivel}</span>
                    </div>
                    {isAdminSistema && (
                      <span className="permissoes-role-badge">Acesso total</span>
                    )}
                  </div>
                  {isExpanded && (
                    <div className="permissoes-role-body">
                      {Object.entries(permsPorRecurso).sort(([a], [b]) => a.localeCompare(b)).map(([recurso, perms]) => {
                        const label = LABELS_RECURSO[recurso] || recurso;
                        const allChecked = perms.every(p => rolePerms.has(p.id));
                        const someChecked = perms.some(p => rolePerms.has(p.id));
                        return (
                          <div key={recurso} className="permissoes-grupo">
                            <div className="permissoes-grupo-header">
                              <label className="permissoes-grupo-check">
                                <input
                                  type="checkbox"
                                  checked={allChecked}
                                  ref={(el) => {
                                    if (el) el.indeterminate = someChecked && !allChecked;
                                  }}
                                  onChange={(e) => toggleAllInResource(role.id, perms, e.target.checked)}
                                  disabled={isAdminSistema}
                                />
                                <span>{label}</span>
                              </label>
                            </div>
                            <div className="permissoes-grupo-itens">
                              {perms.map((p) => (
                                <label key={p.id} className="permissoes-item">
                                  <input
                                    type="checkbox"
                                    checked={rolePerms.has(p.id)}
                                    onChange={() => togglePermission(role.id, p.id)}
                                    disabled={isAdminSistema}
                                  />
                                  <span>{p.descricao || `${p.recurso}.${p.acao}`}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                      {!isAdminSistema && (
                        <div className="permissoes-role-actions">
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => salvarPermissoesRole(role.id)}
                            disabled={permsSaving === role.id}
                          >
                            {permsSaving === role.id ? 'Salvando…' : 'Salvar alterações'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="configuracoes-section configuracoes-danger">
        <h2>Zona de perigo</h2>
        <div className="configuracoes-danger-card">
          <div className="configuracoes-danger-header">
            <FiDatabase size={24} />
            <div>
              <h3>Redefinir base de dados</h3>
              <p>
                Exclui permanentemente todas as licenças, condicionantes e tarefas. Empreendimentos e usuários são mantidos.
              </p>
            </div>
          </div>
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => setShowResetModal(true)}
          >
            Redefinir base de dados
          </button>
        </div>
      </section>

      {showResetModal && (
        <div className="modal-overlay" onClick={() => !resetting && setShowResetModal(false)}>
          <div className="modal-content configuracoes-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Confirmar redefinição da base</h2>
            <p className="configuracoes-modal-warning">
              Esta ação é <strong>irreversível</strong>. Todas as licenças, condicionantes e tarefas serão excluídas permanentemente.
            </p>
            <p>Para confirmar, digite exatamente: <code>{CONFIRMACAO_ESPERADA}</code></p>
            <form onSubmit={handleResetDb}>
              <div className="form-group">
                <input
                  type="text"
                  value={confirmacao}
                  onChange={(e) => setConfirmacao(e.target.value)}
                  placeholder={CONFIRMACAO_ESPERADA}
                  className="form-control"
                  autoComplete="off"
                  disabled={resetting}
                />
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowResetModal(false);
                    setConfirmacao('');
                  }}
                  disabled={resetting}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-danger"
                  disabled={confirmacao !== CONFIRMACAO_ESPERADA || resetting}
                >
                  {resetting ? 'Redefinindo…' : 'Redefinir base de dados'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Configuracoes;
